// Region -> text extraction. The core works on "boxes" (word/run rectangles with a string),
// which come from either the PDF text layer (PDF.js text items mapped through the viewport
// transform) or the DOCX text layer (DOM word boxes from docx.ts). Both share the same
// box model, so text-flow and spatial-table extraction are written once here.
import type { TextContent, TextItem } from 'pdfjs-dist/types/src/display/api'
import { pdfjsLib, type PdfPage } from './pdf'
import type { NormRect } from '../types'

export interface CanvasRect {
  left: number
  top: number
  width: number
  height: number
}

export interface Box {
  left: number
  top: number
  right: number
  bottom: number
  str: string
}

function median(values: number[]): number {
  if (!values.length) return 0
  const s = [...values].sort((a, b) => a - b)
  return s[Math.floor(s.length / 2)]
}

/** Build boxes (in canvas/display space) from a PDF.js text layer. */
function pdfTextBoxes(
  textContent: TextContent,
  viewport: pdfjsLib.PageViewport,
  scale: number,
): Box[] {
  const boxes: Box[] = []
  for (const item of textContent.items) {
    if (!('str' in item)) continue
    const ti = item as TextItem
    if (!ti.str || !ti.str.trim()) continue
    const m = pdfjsLib.Util.transform(viewport.transform, ti.transform)
    const x = m[4]
    const baseline = m[5]
    const w = ti.width * scale
    const h = (ti.height ? ti.height * scale : Math.abs(m[3])) || 1
    boxes.push({ left: x, top: baseline - h, right: x + w, bottom: baseline, str: ti.str })
  }
  return boxes
}

/** Keep only boxes that fall inside `sel` (centre inside, or >35% area overlap). */
function boxesInSelection(boxes: Box[], sel: CanvasRect): Box[] {
  const selRight = sel.left + sel.width
  const selBottom = sel.top + sel.height
  const out: Box[] = []
  for (const box of boxes) {
    const ix = Math.max(0, Math.min(box.right, selRight) - Math.max(box.left, sel.left))
    const iy = Math.max(0, Math.min(box.bottom, selBottom) - Math.max(box.top, sel.top))
    const intersection = ix * iy
    const area = Math.max(1, (box.right - box.left) * (box.bottom - box.top))
    const cx = (box.left + box.right) / 2
    const cy = (box.top + box.bottom) / 2
    const centerInside = cx >= sel.left && cx <= selRight && cy >= sel.top && cy <= selBottom
    if (centerInside || intersection / area > 0.35) out.push(box)
  }
  return out
}

/** Group boxes into visual lines (rows) by baseline proximity. */
function groupLines(boxes: Box[]): Box[][] {
  if (!boxes.length) return []
  boxes.sort((a, b) => a.bottom - b.bottom || a.left - b.left)
  const heights = boxes.map((b) => b.bottom - b.top)
  const lineTol = Math.max(2, median(heights) * 0.6)
  const lines: Box[][] = []
  for (const b of boxes) {
    const current = lines[lines.length - 1]
    if (current && Math.abs(current[0].bottom - b.bottom) <= lineTol) current.push(b)
    else lines.push([b])
  }
  return lines
}

/** Line-flow text from boxes already in the same coordinate space as `sel`. */
export function extractTextFromBoxes(boxes: Box[], sel: CanvasRect): string {
  const lines = groupLines(boxesInSelection(boxes, sel))
  if (!lines.length) return ''
  const rendered = lines.map((line) => {
    line.sort((a, b) => a.left - b.left)
    let out = ''
    for (let i = 0; i < line.length; i++) {
      if (i > 0) {
        const prev = line[i - 1]
        const gap = line[i].left - prev.right
        const charW = (prev.right - prev.left) / Math.max(1, prev.str.length)
        if (gap > charW * 2.5) out += '\t'
        else if (gap > charW * 0.3) out += ' '
      }
      out += line[i].str
    }
    return out.replace(/\s+$/, '')
  })
  return rendered.join('\n').trim()
}

/**
 * Spatial table extraction — infers column separators *globally* from the horizontal gaps
 * that are empty across the whole selection, then assigns each run to a column by its centre
 * x. Keeps columns aligned even when rows are ragged. Output is tab-delimited.
 */
export function extractTableFromBoxes(boxes: Box[], sel: CanvasRect): string {
  const inSel = boxesInSelection(boxes, sel)
  if (!inSel.length) return ''
  const lines = groupLines(inSel)

  const intervals = inSel.map((b) => [b.left, b.right] as [number, number]).sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const [l, r] of intervals) {
    const last = merged[merged.length - 1]
    if (last && l <= last[1] + 0.5) last[1] = Math.max(last[1], r)
    else merged.push([l, r])
  }
  const charW = median(inSel.map((b) => (b.right - b.left) / Math.max(1, b.str.length)))
  const gapTol = Math.max(3, charW * 1.5)
  const seps: number[] = []
  for (let i = 1; i < merged.length; i++) {
    const gap = merged[i][0] - merged[i - 1][1]
    if (gap > gapTol) seps.push((merged[i - 1][1] + merged[i][0]) / 2)
  }
  const colOf = (cx: number): number => {
    let i = 0
    while (i < seps.length && cx > seps[i]) i++
    return i
  }
  const nCols = seps.length + 1
  const rows = lines.map((line) => {
    const cells: string[][] = Array.from({ length: nCols }, () => [])
    line.sort((a, b) => a.left - b.left)
    for (const b of line) cells[colOf((b.left + b.right) / 2)].push(b.str)
    return cells.map((c) => c.join(' ').trim()).join('\t')
  })
  return rows.join('\n').trim()
}

// --- PDF convenience wrappers (build boxes from the text layer, then delegate) ---

export function extractRegionText(
  textContent: TextContent,
  viewport: pdfjsLib.PageViewport,
  scale: number,
  sel: CanvasRect,
): string {
  return extractTextFromBoxes(pdfTextBoxes(textContent, viewport, scale), sel)
}

export function extractRegionTable(
  textContent: TextContent,
  viewport: pdfjsLib.PageViewport,
  scale: number,
  sel: CanvasRect,
): string {
  return extractTableFromBoxes(pdfTextBoxes(textContent, viewport, scale), sel)
}

// --- OCR raster helpers ---

/**
 * Re-render the selected region straight from the PDF at a fixed high DPI for OCR,
 * instead of sampling the on-screen canvas (whose resolution drops when zoomed out).
 */
export async function renderRegionForOcr(
  page: PdfPage,
  norm: NormRect,
  rotation: number,
  targetDpi = 300,
): Promise<HTMLCanvasElement> {
  const vp = page.getViewport({ scale: targetDpi / 72, rotation })
  const sx = Math.round(norm.x * vp.width)
  const sy = Math.round(norm.y * vp.height)
  const sw = Math.max(1, Math.round(norm.w * vp.width))
  const sh = Math.max(1, Math.round(norm.h * vp.height))
  const out = document.createElement('canvas')
  out.width = sw
  out.height = sh
  const ctx = out.getContext('2d')!
  await page.render({ canvasContext: ctx, viewport: vp, transform: [1, 0, 0, 1, -sx, -sy] }).promise
  return out
}

/** Crop an OCR region from a raster image at its native resolution (honouring rotation). */
export function renderImageRegionForOcr(
  img: HTMLImageElement,
  norm: NormRect,
  rotation: number,
): HTMLCanvasElement {
  const swap = rotation === 90 || rotation === 270
  const w = swap ? img.naturalHeight : img.naturalWidth
  const h = swap ? img.naturalWidth : img.naturalHeight
  const full = document.createElement('canvas')
  full.width = Math.max(1, w)
  full.height = Math.max(1, h)
  const fctx = full.getContext('2d')!
  fctx.translate(w / 2, h / 2)
  fctx.rotate((rotation * Math.PI) / 180)
  fctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2)
  return cropFromCanvas(full, norm)
}

/** Crop a normalized region from an already-rendered canvas (used for DOCX page rasters). */
export function cropFromCanvas(source: HTMLCanvasElement, norm: NormRect): HTMLCanvasElement {
  const sx = Math.round(norm.x * source.width)
  const sy = Math.round(norm.y * source.height)
  const sw = Math.max(1, Math.round(norm.w * source.width))
  const sh = Math.max(1, Math.round(norm.h * source.height))
  const out = document.createElement('canvas')
  out.width = sw
  out.height = sh
  out.getContext('2d')!.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh)
  return out
}

/** Crop a region of the rendered page canvas into a new canvas (fallback OCR path). */
export function cropCanvas(source: HTMLCanvasElement, sel: CanvasRect, upscale = 2): HTMLCanvasElement {
  const out = document.createElement('canvas')
  out.width = Math.max(1, Math.round(sel.width * upscale))
  out.height = Math.max(1, Math.round(sel.height * upscale))
  const ctx = out.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, sel.left, sel.top, sel.width, sel.height, 0, 0, out.width, out.height)
  return out
}

export type { PdfPage }
